import {
  Component,
  inject,
  AfterViewInit,
  OnInit,
  ChangeDetectionStrategy,
  effect,
  ViewChild,
  TemplateRef
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  AsyncValidatorFn,
  Validators
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { catchError, debounceTime, distinctUntilChanged, first, of, switchMap, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import {
  commercialRegisterNumberValidator,
  completeDateValidator,
  emailMatchValidator,
  pdfFileValidator,
  taxIdNumberValidator,
  urlValidator
} from '../../../../services/form/form-validators';
import { NotificationService } from '../../../../services/form/notification.service';
import { RegistrationService } from '../../../../services/registration/registration.service';
import { NavigationService } from '../../../../services/navigation.service';
import { MatIconModule } from '@angular/material/icon';
import { StepperComponent, StepperStep, StepHeaderComponent } from 'htx-ui-library';
import { RegistrationStep0PrefaceComponent } from '../registration-step-0-preface/registration-step-0-preface.component';
import { RegistrationStep1UserComponent } from '../registration-step-1-user/registration-step-1-user.component';
import { RegistrationStep2OrganisationComponent } from '../registration-step-2-organisation/registration-step-2-organisation.component';
import { RegistrationStep3VerificationComponent } from '../registration-step-3-verification/registration-step-3-verification.component';
import { RegistrationStep4SummaryComponent } from '../registration-step-4-summary/registration-step-4-summary.component';

@Component({
  selector: 'app-register',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoDirective,
    StepperComponent,
    MatIconModule,
    RegistrationStep0PrefaceComponent,
    RegistrationStep1UserComponent,
    RegistrationStep2OrganisationComponent,
    RegistrationStep3VerificationComponent,
    RegistrationStep4SummaryComponent
  ],
  templateUrl: './registration-page.component.html',
  styleUrl: './registration-page.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationPageComponent implements OnInit, AfterViewInit {
  readonly fb = inject(FormBuilder);
  readonly router = inject(Router);
  readonly registrationService = inject(RegistrationService);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly translocoService = inject(TranslocoService);
  readonly notificationService = inject(NotificationService);
  readonly viewportScroller = inject(ViewportScroller);
  private readonly navigationService = inject(NavigationService);

  ngOnInit(): void {
    this.navigationService.redirectIfLoggedIn();
  }

  @ViewChild('step0Template') step0Template!: TemplateRef<any>;
  @ViewChild('step1Template') step1Template!: TemplateRef<any>;
  @ViewChild('step2Template') step2Template!: TemplateRef<any>;
  @ViewChild('step3Template') step3Template!: TemplateRef<any>;
  @ViewChild('step4Template') step4Template!: TemplateRef<any>;

  getStepTemplate(): TemplateRef<any> {
    switch (this.currentStep()) {
      case 0:
        return this.step0Template;
      case 1:
        return this.step1Template;
      case 2:
        return this.step2Template;
      case 3:
        return this.step3Template;
      case 4:
        return this.step4Template;
      default:
        return this.step0Template;
    }
  }

  userFormFieldLabels: { [key: string]: string } = {};
  organisationFormFieldLabels: { [key: string]: string } = {};
  verificationFormFieldLabels: { [key: string]: string } = {};

  prefaceForm = this.fb.group({
    confirmation: [false, [Validators.requiredTrue]]
  });

  userForm = this.fb.group({
    salutation: [''],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    position: [''],
    phoneNumber: [''],
    email: ['', [Validators.required, Validators.email]],
    emailConfirm: ['', [Validators.required, Validators.email, emailMatchValidator('email')]],
    hasAgreedToDataProtectionDeclaration: [false, [Validators.requiredTrue]]
  });
  organisationForm = this.fb.group({
    legalName: ['', [Validators.required]],
    shortName: [
      '',
      [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20),
        Validators.pattern(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/)
      ]
    ],
    legalForm: ['', Validators.required],
    sector: [''],
    website: ['', [Validators.required, urlValidator]],
    street: ['', [Validators.required]],
    houseNumber: ['', [Validators.required]],
    postalCode: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]],
    city: ['', [Validators.required]],
    description: ['', [Validators.maxLength(500)]]
  });

  verificationForm = this.fb.group({
    foundingDate: [null as unknown as Date, [Validators.required, completeDateValidator]],
    commercialRegisterCourt: ['', [Validators.required]],
    commercialRegisterNumber: ['', [Validators.required, commercialRegisterNumberValidator]],
    taxIdNumber: ['', [Validators.required, taxIdNumberValidator]],
    hasAgreedToMembershipDeclaration: [false, Validators.requiredTrue],
    document: [null as unknown as File, [Validators.required, pdfFileValidator]]
  });

  onEditStep(step: number): void {
    this.editMode.set(true);
    this.currentStep.set(step);
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  onPrevious() {
    const previous = this.currentStep() - 1;
    if (previous >= 0) {
      this.currentStep.set(previous);
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  onNext() {
    if (this.isCurrentStepValid()) {
      const next = this.editMode() ? 4 : this.currentStep() + 1;
      this.editMode.set(false);
      if (next < this.registrationSteps().length) {
        this.currentStep.set(next);
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    }
  }

  onSubmit() {
    // If we're at step 4 (summary), submit the registration
    if (this.currentStep() === 4) {
      this.finishRegistration();
    }
  }

  finishRegistration() {
    if (this.userForm.invalid || this.organisationForm.invalid || this.verificationForm.invalid) {
      return;
    }

    const userVal = this.userForm.value;
    const orgVal = this.organisationForm.value;
    const verVal = this.verificationForm.value;

    this.registrationService
      .register({
        title: userVal.salutation!,
        firstName: userVal.firstName!,
        lastName: userVal.lastName!,
        position: userVal.position!,
        phoneNumber: userVal.phoneNumber!,
        email: userVal.email!,
        hasAgreedToDataProtectionDeclaration: userVal.hasAgreedToDataProtectionDeclaration!,
        shortName: orgVal.shortName!,
        legalName: orgVal.legalName!,
        legalForm: orgVal.legalForm!,
        sector: orgVal.sector!,
        website: orgVal.website!,
        street: orgVal.street!,
        houseNumber: orgVal.houseNumber!,
        postalCode: orgVal.postalCode!,
        city: orgVal.city!,
        description: orgVal.description!,
        foundingDate: verVal.foundingDate!,
        commercialRegisterCourt: verVal.commercialRegisterCourt!,
        commercialRegisterNumber: verVal.commercialRegisterNumber!,
        taxIdNumber: verVal.taxIdNumber!,
        hasAgreedToMembershipDeclaration: verVal.hasAgreedToMembershipDeclaration!,
        document: verVal.document!
      })
      .subscribe({
        next: () => {
          void this.router.navigate(['./completed'], {
            relativeTo: this.activatedRoute
          });
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 409) {
            const taken = orgVal.shortName;
            if (taken) {
              this.takenShortNames.add(taken.trim().toLowerCase());
            }
            const shortNameCtrl = this.organisationForm.controls['shortName'];
            shortNameCtrl.updateValueAndValidity();
            shortNameCtrl.markAsTouched();
            this.currentStep.set(2);
            this.viewportScroller.scrollToPosition([0, 0]);
            this.notificationService.showError('registration.page.errors.shortNameTaken');
            return;
          }
          this.notificationService.showError(
            'Registrierung fehlgeschlagen. Bitte versuchen Sie es später erneut.'
          );
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.verificationForm.patchValue({
        document: files[0]
      });
      //this.fileUploadService.cachedDocument = files[0];
    }
  }

  onAutoFillRequested(): void {
    const randomName = Math.random().toString(36).substring(0, 10);
    const randomHrb = this.generateRandomHRB();
    const randomTaxId = this.generateRandomTaxId();

    // Fill user form
    this.userForm.patchValue({
      salutation: 'prof',
      firstName: 'John',
      lastName: 'Doe',
      position: 'CEO',
      phoneNumber: '+49 123 456 789',
      email: `${randomName}@test-${randomName}.com`,
      emailConfirm: `${randomName}@test-${randomName}.com`,
      hasAgreedToDataProtectionDeclaration: true
    });

    // Fill organisation form
    this.organisationForm.patchValue({
      legalName: 'Test Organisation',
      shortName: 'TestOrg',
      legalForm: 'GmbH',
      sector: 'Technology',
      website: `https://test-${randomName}.de`,
      street: 'Teststrasse',
      houseNumber: '123',
      postalCode: '10115',
      city: 'Berlin',
      description: 'Test Organisation für Demo'
    });

    // Fill verification form
    this.verificationForm.patchValue({
      foundingDate: new Date('01.01.2020'),
      commercialRegisterCourt: 'Berlin',
      commercialRegisterNumber: randomHrb,
      taxIdNumber: randomTaxId,
      hasAgreedToMembershipDeclaration: true
    });
  }

  private generateRandomHRB(): string {
    const randomNumber = Math.floor(Math.random() * 999999)
      .toString()
      .padStart(6, '0');
    return `HRB${randomNumber}`;
  }

  private generateRandomTaxId(): string {
    const countryCode = 'DE';
    const randomNumber = Math.floor(Math.random() * 999999999)
      .toString()
      .padStart(9, '0');
    return `${countryCode}${randomNumber}`;
  }

  registrationSteps = signal<StepperStep[]>([]);
  currentStep = signal<number>(0);
  isCurrentStepValid = signal<boolean>(false);
  editMode = signal<boolean>(false);

  private readonly takenShortNames = new Set<string>();

  private readonly shortNameTakenValidator: ValidatorFn = (control) => {
    const value = control.value;
    if (typeof value === 'string' && this.takenShortNames.has(value.trim().toLowerCase())) {
      return { shortNameTaken: true };
    }
    return null;
  };

  private readonly shortNameAvailableAsyncValidator: AsyncValidatorFn = (control) => {
    const value = control.value;
    if (typeof value !== 'string' || value.trim().length < 3) {
      return of(null);
    }
    // Skip if the synchronous shortName validators already fail (e.g. pattern)
    const syncErrors = control.errors;
    if (
      syncErrors &&
      (syncErrors['pattern'] || syncErrors['minlength'] || syncErrors['maxlength'])
    ) {
      return of(null);
    }
    return timer(400).pipe(
      switchMap(() =>
        this.registrationService.isShortNameAvailable(value.trim()).pipe(
          map((available) => (available ? null : { shortNameTaken: true })),
          catchError(() => of(null))
        )
      ),
      first()
    );
  };

  constructor() {
    const shortNameCtrl = this.organisationForm.controls['shortName'];
    shortNameCtrl.addValidators(this.shortNameTakenValidator);
    shortNameCtrl.addAsyncValidators(this.shortNameAvailableAsyncValidator);
    shortNameCtrl.valueChanges
      .pipe(
        map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : '')),
        distinctUntilChanged(),
        debounceTime(0)
      )
      .subscribe();
    // Track form validity changes and update the signal
    effect(() => {
      const step = this.currentStep();
      let isValid = false;

      switch (step) {
        case 0:
          isValid = this.prefaceForm.valid;
          break;
        case 1:
          isValid = this.userForm.valid;
          break;
        case 2:
          isValid = this.organisationForm.valid;
          break;
        case 3:
          isValid = this.verificationForm.valid;
          break;
        case 4:
          isValid = true; // Summary doesn't need validation
          break;
      }

      this.isCurrentStepValid.set(isValid);
    });

    // Subscribe to form value changes to trigger validity updates
    this.prefaceForm.statusChanges.subscribe(() => {
      if (this.currentStep() === 0) {
        this.isCurrentStepValid.set(this.prefaceForm.valid);
      }
    });

    this.userForm.statusChanges.subscribe(() => {
      if (this.currentStep() === 1) {
        this.isCurrentStepValid.set(this.userForm.valid);
      }
    });

    // Revalidate emailConfirm when email changes
    this.userForm.controls['email'].valueChanges.subscribe(() => {
      this.userForm.controls['emailConfirm'].updateValueAndValidity();
    });

    this.organisationForm.statusChanges.subscribe(() => {
      if (this.currentStep() === 2) {
        this.isCurrentStepValid.set(this.organisationForm.valid);
      }
    });

    this.verificationForm.statusChanges.subscribe(() => {
      if (this.currentStep() === 3) {
        this.isCurrentStepValid.set(this.verificationForm.valid);
      }
    });
  }

  get registrationStepsWithContent(): StepperStep[] {
    return this.registrationSteps();
  }

  ngAfterViewInit(): void {
    // Sammle alle Übersetzungs-Keys
    const stepConfigs = [
      { prefix: 'prefaceForm', icon: 'description', hideHeader: true },
      { prefix: 'userForm', icon: 'person' },
      { prefix: 'organisationForm', icon: 'business' },
      { prefix: 'verificationForm', icon: 'verified' },
      { prefix: 'summary', icon: 'check_circle' }
    ];

    const translationKeys = stepConfigs.flatMap((config) =>
      ['stepLabel', 'title', 'description'].map(
        (key) => `registration.page.${config.prefix}.${key}`
      )
    );

    // Lade alle Übersetzungen und erstelle die Steps + Field Labels
    this.translocoService
      .selectTranslate(translationKeys)
      .pipe(
        map(() => {
          // Load field labels from translations - helper function
          const loadFieldLabels = (formName: string, controlNames: string[]) => {
            const labels: { [key: string]: string } = {};
            controlNames.forEach((controlName) => {
              // Try .label first, then .text for checkboxes
              const labelKey = `registration.page.${formName}.controls.${controlName}.label`;
              const textKey = `registration.page.${formName}.controls.${controlName}.text`;

              const labelValue = this.translocoService.translate(labelKey);
              // If it's a translation key (contains dots and starts with prefix), try .text
              if (labelValue.includes('.') || labelValue === labelKey) {
                labels[controlName] = this.translocoService.translate(textKey);
              } else {
                labels[controlName] = labelValue;
              }
            });
            return labels;
          };

          this.userFormFieldLabels = loadFieldLabels(
            'userForm',
            Object.keys(this.userForm.controls)
          );
          this.organisationFormFieldLabels = loadFieldLabels(
            'organisationForm',
            Object.keys(this.organisationForm.controls)
          );
          this.verificationFormFieldLabels = loadFieldLabels(
            'verificationForm',
            Object.keys(this.verificationForm.controls)
          );

          return stepConfigs.map((config) => ({
            label: this.translocoService.translate(`registration.page.${config.prefix}.stepLabel`),
            badge: '',
            title: this.translocoService.translate(`registration.page.${config.prefix}.title`),
            description: [
              this.translocoService.translate(`registration.page.${config.prefix}.description`)
            ],
            icon: config.icon,
            hideHeader: config.hideHeader,
            completed: false
          }));
        })
      )
      .subscribe((steps) => this.registrationSteps.set(steps));
  }
}
